/**
 * Custom hook for consistent error handling across components
 *
 * Usage:
 * const { handleApiError, clearError } = useErrorHandler();
 *
 * try {
 *   await api.call();
 * } catch (error) {
 *   handleApiError(error);
 * }
 */

import { useCallback } from 'react';
import { useToast } from '../contexts/ToastContext';
import { handleError } from '../services/errorHandler';

export const useErrorHandler = () => {
  const toast = useToast();

  /**
   * Handle API errors with toast notifications
   */
  const handleApiError = useCallback((error, options = {}) => {
    const {
      showToast: shouldShowToast = true,
      customMessage = null,
      onError = null,
    } = options;

    // Process error with centralized handler
    const errorInfo = error.errorInfo || handleError(error, {
      customMessage,
    });

    // Show toast if needed
    if (shouldShowToast && errorInfo.shouldShowToUser) {
      const toastType = errorInfo.severity === 'warning' ? 'warning' : 'error';

      toast.showToast(errorInfo.message, {
        type: toastType,
        duration: 6000,
      });
    }

    // Call custom error handler if provided
    if (onError) {
      onError(errorInfo);
    }

    return errorInfo;
  }, [toast]);

  /**
   * Handle form validation errors
   */
  const handleValidationError = useCallback((error) => {
    const errorInfo = handleApiError(error, { showToast: false });

    if (errorInfo.validationErrors) {
      // Return field errors for form display
      return errorInfo.validationErrors;
    }

    // If no field errors, show general error toast
    toast.showError(errorInfo.message);
    return null;
  }, [handleApiError, toast]);

  /**
   * Show success message
   */
  const showSuccess = useCallback((message, options = {}) => {
    toast.showSuccess(message, options);
  }, [toast]);

  /**
   * Show info message
   */
  const showInfo = useCallback((message, options = {}) => {
    toast.showInfo(message, options);
  }, [toast]);

  /**
   * Show warning message
   */
  const showWarning = useCallback((message, options = {}) => {
    toast.showWarning(message, options);
  }, [toast]);

  return {
    handleApiError,
    handleValidationError,
    showSuccess,
    showInfo,
    showWarning,
  };
};

export default useErrorHandler;
